/**
 * src/components/ui/Button.tsx
 * ------------------------------
 * Reusable accessible button component with variant and size support.
 *
 * Fully implemented this will support:
 *   Variants:
 *     primary   — filled primary color (default)
 *     secondary — outlined, transparent background
 *     ghost     — transparent, hover background only
 *     danger    — filled red, for destructive actions
 *   Sizes:
 *     sm — text-xs, px-3 py-1.5
 *     md — text-sm, px-4 py-2 (default)
 *     lg — text-base, px-6 py-3
 *
 *   - Forward ref for external focus management.
 *   - Loading state: shows spinner, disables interaction.
 *   - Full-width via className.
 *   - Accepts all native <button> props.
 *
 * Key dependencies: clsx, tailwind-merge (via src/lib/utils.ts)
 */
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import type { ButtonHTMLAttributes } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

const VARIANT_STYLES: Record<string, string> = {
  primary: 'cx-ui-button-primary',
  secondary: 'cx-ui-button-secondary',
  ghost: 'cx-ui-button-ghost',
  danger: 'cx-ui-button-danger',
}

const SIZE_STYLES: Record<string, string> = {
  sm: 'cx-ui-button-sm',
  md: 'cx-ui-button-md',
  lg: 'cx-ui-button-lg',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'cx-ui-button',
          VARIANT_STYLES[variant],
          SIZE_STYLES[size],
          className,
        )}
        {...props}
      >
        {loading && (
          <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
        )}
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'
